<template>
  <div class="credit-pack-option-list">
    <div>
      <h3 class="credit-pack-option-list__heading">Select credit pack</h3>
      <BittsBone v-if="isLoading" :height="180" shimmer="slow" />
      <BittsCallout
        v-else-if="hasFailed"
        type="danger"
        size="x-small"
        role="alert"
        title="We couldn't load credit packs"
        subtitle="Check your connection and try again."
        action-text="Try again"
        data-testid="credit-pack-catalogue-failure"
        @bitts-callout-action="emit('retry')"
      />
      <BittsRadioGroupCards
        v-else-if="packs.length"
        :options="options"
        :initial-value="selectedPricingKey ?? undefined"
        :disabled="isDisabled"
        @change="emit('select', $event)"
      >
        <template #label="{ option }">
          <span class="credit-pack-option-list__credits">
            {{ formatNumberWithCommas(option.pack.credits) }}
          </span>
          <span class="credit-pack-option-list__price">
            {{ packPrice(option.pack) }}
            <span class="text-neutral-text-placeholder">&sdot;</span>
            {{ perCreditRate(option.pack) }} / credit
          </span>
        </template>
        <template #right="{ option }">
          <BittsTag
            v-if="option.isBestValue"
            color="secondary"
            size="x-small"
            text="Best value"
            :data-testid="`credit-pack-best-value-${option.value}`"
          />
        </template>
      </BittsRadioGroupCards>
    </div>
    <p class="credit-pack-option-list__note">
      Credit packs are a recurring annual add-on. These credits are added to
      your balance for the current contract term and expire at your contract end
      date, with no carryover. The pack renews with your subscription at your
      next contract term.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  BittsBone,
  BittsCallout,
  BittsRadioGroupCards,
  BittsTag,
} from '@crossbeam/bitts';

import { computed } from 'vue';

import type { CreditPack } from '@/types/billing';
import { currencyFormatter, formatNumberWithCommas } from '@/utils';

const props = defineProps<{
  packs: CreditPack[];
  selectedPricingKey: string | null;
  bestValuePricingKey: string;
  isDisabled: boolean;
  isLoading: boolean;
  hasFailed: boolean;
}>();

const emit = defineEmits<{ select: [pricingKey: string]; retry: [] }>();

const options = computed(() =>
  props.packs.map((pack) => ({
    value: pack.pricing_key,
    pack,
    isBestValue: pack.pricing_key === props.bestValuePricingKey,
  })),
);

function packPrice(pack: CreditPack) {
  return currencyFormatter(pack.unit_amount_cents / 100);
}

function perCreditRate(pack: CreditPack) {
  const centsPerCredit = pack.unit_amount_cents / pack.credits;
  return currencyFormatter(centsPerCredit / 100, true);
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.credit-pack-option-list {
  @apply flex flex-col gap-24 p-12;
}
.credit-pack-option-list__heading {
  @apply text-neutral-text-strong text-m font-bold mb-16;
}
.credit-pack-option-list__credits {
  @apply text-neutral-text-strong font-bold block;
}
.credit-pack-option-list__price {
  @apply text-neutral-text-weak text-sm block;
}
.credit-pack-option-list__note {
  @apply text-neutral-text text-sm;
}
</style>
